import React, { useCallback, useEffect, useState } from "react";
import StageHeader from "./page/StageHeader";
import SubStepNav from "./page/SubStepNav";
import { useMsal } from "@azure/msal-react";
import {
  AuditOutlined,
  CalendarOutlined,
  FileTextOutlined,
  FormOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { apiTokenRequest } from "../authConfig";
import { useApp } from "../context/AppContext";
import { WorkflowSubTab } from "./internalAudit/WorkflowSubTab";
import { AuditPlanSubTab } from "./internalAudit/AuditPlanSubTab";
import { QuestionnaireSubTab } from "./internalAudit/QuestionnaireSubTab";
import { AuditFindingsSubTab } from "./internalAudit/AuditFindingsSubTab";
import { CorrectiveActionsSubTab } from "./internalAudit/CorrectiveActionsSubTab";
import { AuditReportSubTab } from "./internalAudit/AuditReportSubTab";
import { Role } from "../constants/roles";
import { canSignOffInternalAudit } from "../utils/approvalGates";
import { userMessageForError } from "../utils/apiError";
import { iaRequest } from "./internalAudit/iaApi";

const SUB_TABS = [
  { key: "workflow", label: "Workflow", icon: ThunderboltOutlined },
  { key: "audit_plan", label: "Audit Plan", icon: CalendarOutlined },
  { key: "questionnaire", label: "Questionnaire", icon: FormOutlined },
  { key: "audit_findings", label: "Audit Findings", icon: AuditOutlined },
  { key: "corrective_actions", label: "Corrective Actions", icon: SafetyCertificateOutlined },
  { key: "audit_report", label: "Audit Report", icon: FileTextOutlined },
];

function SummaryCard({ label, value, tone }) {
  const tones = {
    blue: "border-[color:var(--color-info-line)] bg-[color:var(--color-info-bg)] text-[color:var(--color-accent)]",
    green: "border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)]",
    red: "border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)]",
    amber: "border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)]",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 text-center shadow-sm ${tones[tone] || tones.blue}`}>
      <div className="text-xl font-black tracking-tight">{value}</div>
      <div className="mt-0.5 text-[10px] font-black uppercase tracking-widest opacity-80">{label}</div>
    </div>
  );
}

/**
 * Internal Audit — workspace step 9 (phase 1: workflow + audit plan).
 */
export function InternalAudit({ project, user }) {
  const { instance } = useMsal();
  const { updateProjectProfile, fetchProjectProfile } = useApp();
  const [activeTab, setActiveTab] = useState("workflow");
  const [loading, setLoading] = useState(true);
  const [pkg, setPkg] = useState(null);
  const [canEditPlan, setCanEditPlan] = useState(false);
  // Circulate plan / close CAP / management approval: Lead Advisor sign-off. platform_admin is edit-capable but
  // refused 403 PLATFORM_ADMIN_APPROVAL_DENIED (PRD s2.2), so those controls are not rendered for it.
  const canSignOff = canSignOffInternalAudit(user?.role);
  const [canViewPlan, setCanViewPlan] = useState(false);
  // null until the API has answered: the cards show "—", never a zero that was not counted.
  const [summaryStats, setSummaryStats] = useState(null);
  const [loadError, setLoadError] = useState("");
  // Naming the auditor / auditee / lead auditor is a Lead Advisor / Advisor act (API: 403 for platform_admin, SPOC,
  // delegate). It is a separate gate from canEditPlan, which an Advisor does not have.
  const canEditNames = user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADVISOR;

  const projectId = project?.id;

  const getApiToken = useCallback(async () => {
    const resp = await instance.acquireTokenSilent({
      ...apiTokenRequest,
      account: instance.getActiveAccount(),
    });
    return resp.accessToken;
  }, [instance]);

  const applyPackage = useCallback(
    (nextPkg) => {
      setPkg(nextPkg);
      if (projectId && updateProjectProfile) {
        const base = project?.profile || {};
        updateProjectProfile(projectId, { ...base, internalAuditPackage: nextPkg });
      }
    },
    [projectId, project?.profile, updateProjectProfile],
  );

  const fetchInternalAudit = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError("");
    try {
      const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit`);
      if (data?.internalAuditPackage) applyPackage(data.internalAuditPackage);
      setCanEditPlan(!!data?.canEditPlan);
      setCanViewPlan(!!data?.canViewPlan);
      if (data?.summaryStats) setSummaryStats(data.summaryStats);
    } catch (e) {
      setLoadError(userMessageForError(e, "Could not load internal audit."));
    } finally {
      setLoading(false);
    }
  }, [projectId, getApiToken, applyPackage]);

  useEffect(() => {
    void fetchInternalAudit();
  }, [fetchInternalAudit]);

  const handlePackageUpdated = useCallback(
    async (nextPkg) => {
      applyPackage(nextPkg);
      setCanViewPlan(true);
      if (fetchProjectProfile && projectId) await fetchProjectProfile(projectId);
      try {
        const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit`);
        if (data?.summaryStats) setSummaryStats(data.summaryStats);
      } catch {
        /* summary refresh is best-effort */
      }
    },
    [applyPackage, fetchProjectProfile, projectId, getApiToken],
  );

  // Never render a tab body before the API has answered (or after it failed): an empty package would read
  // as "plan not circulated" / "no findings" when the truth is "unknown".
  const showTabs = !loadError && !(loading && !pkg);
  const rows = pkg?.auditPlanRows || [];
  const planWorkflow = pkg?.planWorkflow || "draft";

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <StageHeader title="Internal Audit" />
          <p className="mt-1 text-sm font-medium text-[color:var(--color-text-muted)]">
            Conduct and manage the ISO 27001 internal audit lifecycle.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard label="Responses" value={summaryStats?.responses ?? "-"} tone="blue" />
          <SummaryCard label="Conformities" value={summaryStats?.conformities ?? "-"} tone="green" />
          <SummaryCard label="NCs" value={summaryStats?.nonConformities ?? "-"} tone="red" />
          <SummaryCard label="Open CAPs" value={summaryStats?.openCaps ?? "-"} tone="amber" />
        </div>
      </div>

      <SubStepNav
        ariaLabel="Internal audit sections"
        steps={SUB_TABS.filter((tab) => {
          // Corrective actions are also open to the Advisor (dates only; every other field stays read-only for them).
          if (tab.key === "corrective_actions") return canEditPlan || canEditNames;
          if (["questionnaire", "audit_findings"].includes(tab.key)) {
            return canEditPlan;
          }
          return true;
        }).map((tab) => ({ key: tab.key, label: tab.label }))}
        activeKey={activeTab}
        onSelect={setActiveTab}
      />

      {loading && !pkg ? (
        <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-16 text-center text-sm font-medium text-[color:var(--color-text-muted)]">
          Loading internal audit…
        </div>
      ) : null}

      {loadError ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-danger-fg)]"
        >
          <span>{loadError}</span>
          <Button size="small" onClick={() => void fetchInternalAudit()}>
            Retry
          </Button>
        </div>
      ) : null}

      {showTabs ? (
        <>
          {activeTab === "workflow" && <WorkflowSubTab pkg={pkg} />}

          {activeTab === "audit_plan" && (
            <AuditPlanSubTab
              projectId={projectId}
              rows={rows}
              planWorkflow={planWorkflow}
              canEditPlan={canEditPlan}
              canEditNames={canEditNames}
              leadAuditor={pkg?.leadAuditor || ""}
              canCirculate={canEditPlan && canSignOff}
              canViewPlan={canViewPlan}
              getApiToken={getApiToken}
              onPackageUpdated={handlePackageUpdated}
            />
          )}

          {activeTab === "questionnaire" && canEditPlan && (
            <QuestionnaireSubTab
              projectId={projectId}
              questionnaire={pkg?.questionnaire}
              canEdit={canEditPlan}
              getApiToken={getApiToken}
              onPackageUpdated={handlePackageUpdated}
              onSwitchTab={setActiveTab}
            />
          )}
          {activeTab === "audit_findings" && canEditPlan && (
            <AuditFindingsSubTab
              projectId={projectId}
              auditFindings={pkg?.auditFindings}
              canEdit={canEditPlan}
              getApiToken={getApiToken}
              onPackageUpdated={handlePackageUpdated}
              onSwitchTab={setActiveTab}
            />
          )}
          {activeTab === "corrective_actions" && (canEditPlan || canEditNames) && (
            <CorrectiveActionsSubTab
              projectId={projectId}
              correctiveActions={pkg?.correctiveActions}
              canEdit={canEditPlan}
              canApprove={canSignOff}
              canSetDates={canEditNames}
              getApiToken={getApiToken}
              onPackageUpdated={handlePackageUpdated}
            />
          )}
          {activeTab === "audit_report" && (
            <AuditReportSubTab
              projectId={projectId}
              project={project}
              canGenerate={canEditPlan && canSignOff}
              canWriteText={canEditNames}
              canSpocSignOff={user?.role === Role.COMPANY_SPOC}
              getApiToken={getApiToken}
              onPackageUpdated={handlePackageUpdated}
              onSwitchTab={setActiveTab}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

export default InternalAudit;
